<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Final Review & Approval</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/hr-dashboard">HR Dashboard</router-link></li>
                    <li class="breadcrumb-item active">Final Review</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading appraisal...</p>
          </div>

          <!-- Review Form -->
          <div v-else-if="appraisal">

            <!-- Summary Card -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card border-success">
                  <div class="card-header bg-success text-white">
                    <h5 class="mb-0 text-white">Appraisal Summary</h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-4">
                        <label class="fw-bold">Employee:</label>
                        <p class="mb-2">{{ appraisal.employeeName }}</p>
                        <label class="fw-bold">Position:</label>
                        <p class="mb-2">{{ appraisal.positionTitle }}</p>
                        <label class="fw-bold">Department:</label>
                        <p class="mb-0">{{ appraisal.department }}</p>
                      </div>
                      <div class="col-md-4">
                        <label class="fw-bold">Review Period:</label>
                        <p class="mb-2">{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</p>
                        <label class="fw-bold">Appraiser:</label>
                        <p class="mb-2">{{ appraisal.appraiserName || 'Not assigned' }}</p>
                        <label class="fw-bold">Current Status:</label>
                        <p class="mb-0">
                          <span :class="getStatusBadgeClass(appraisal.status)" class="badge">
                            {{ appraisal.status }}
                          </span>
                        </p>
                      </div>
                      <div class="col-md-4">
                        <div class="text-center bg-light p-3 rounded">
                          <h6 class="mb-2">Overall Ratings</h6>
                          <div class="row">
                            <div class="col-6">
                              <label class="text-muted small">Self</label>
                              <h5 class="text-primary mb-0">
                                {{ appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-' }}
                              </h5>
                            </div>
                            <div class="col-6">
                              <label class="text-muted small">Manager</label>
                              <h5 class="text-info mb-0">
                                {{ appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-' }}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Detailed Review (Expandable Sections) -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="accordion" id="appraisalAccordion">

                  <!-- Section A: KPAs -->
                  <div class="accordion-item" v-if="kpas.length > 0">
                    <h2 class="accordion-header" id="headingKPAs">
                      <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseKPAs">
                        <i class="mdi mdi-target me-2"></i> Section A: Key Performance Areas ({{ kpas.length }} items)
                      </button>
                    </h2>
                    <div id="collapseKPAs" class="accordion-collapse collapse show" data-bs-parent="#appraisalAccordion">
                      <div class="accordion-body">
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="table-light">
                            <tr>
                              <th width="3%">#</th>
                              <th width="25%">KPA</th>
                              <th width="20%">Results</th>
                              <th width="8%">Self</th>
                              <th width="8%">Manager</th>
                              <th width="36%">Manager Comments</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="kpa in kpas" :key="kpa.id">
                              <td class="text-center">{{ kpa.sNo }}</td>
                              <td>{{ kpa.kpa }}</td>
                              <td><small>{{ kpa.resultsAchieved || '-' }}</small></td>
                              <td class="text-center">
                                <span class="badge bg-primary">{{ kpa.selfRating }}</span>
                              </td>
                              <td class="text-center">
                                <span class="badge bg-success">{{ kpa.appraiserRating }}</span>
                              </td>
                              <td><small>{{ kpa.appraiserComment || '-' }}</small></td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Section B: Attributes -->
                  <div class="accordion-item" v-if="attributes.length > 0">
                    <h2 class="accordion-header" id="headingAttributes">
                      <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAttributes">
                        <i class="mdi mdi-star me-2"></i> Section B: Critical Success Attributes ({{ attributes.length }} items)
                      </button>
                    </h2>
                    <div id="collapseAttributes" class="accordion-collapse collapse" data-bs-parent="#appraisalAccordion">
                      <div class="accordion-body">
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="table-light">
                            <tr>
                              <th width="5%">#</th>
                              <th width="30%">Attribute</th>
                              <th width="10%">Self</th>
                              <th width="10%">Manager</th>
                              <th width="45%">Manager Comments</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="attr in attributes" :key="attr.id">
                              <td class="text-center">{{ attr.sNo }}</td>
                              <td>{{ attr.attribute }}</td>
                              <td class="text-center">
                                <span class="badge bg-primary">{{ attr.selfRating }}</span>
                              </td>
                              <td class="text-center">
                                <span class="badge bg-success">{{ attr.appraiserRating }}</span>
                              </td>
                              <td><small>{{ attr.appraiserComment || '-' }}</small></td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Employee Feedback -->
                  <div class="accordion-item">
                    <h2 class="accordion-header" id="headingFeedback">
                      <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFeedback">
                        <i class="mdi mdi-comment-text me-2"></i> Employee Feedback & Goals
                      </button>
                    </h2>
                    <div id="collapseFeedback" class="accordion-collapse collapse" data-bs-parent="#appraisalAccordion">
                      <div class="accordion-body">
                        <!-- Training Priorities -->
                        <div v-if="trainings.length > 0" class="mb-3">
                          <h6>Training Priorities:</h6>
                          <ul>
                            <li v-for="training in trainings" :key="training.id">
                              {{ training.trainingProgram }} (Priority: {{ training.priority }})
                            </li>
                          </ul>
                        </div>

                        <!-- Goals -->
                        <div v-if="goals.length > 0" class="mb-3">
                          <h6>Goals for Next Period:</h6>
                          <ol>
                            <li v-for="goal in goals" :key="goal.id">
                              {{ goal.actionPlan }} (Target: {{ formatDate(goal.targetDate) }})
                            </li>
                          </ol>
                        </div>

                        <!-- Comments -->
                        <div v-if="appraisal.associateComments">
                          <h6>Employee Comments:</h6>
                          <div class="bg-light p-3 rounded">
                            {{ appraisal.associateComments }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Final Review Section -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card border-warning">
                  <div class="card-header bg-warning">
                    <h5 class="mb-0">HR/Reviewer Final Assessment</h5>
                  </div>
                  <div class="card-body">

                    <div class="row mb-3">
                      <div class="col-md-6">
                        <label class="form-label fw-bold">Final Overall Rating (1-5) <span class="text-danger">*</span></label>
                        <input
                            type="number"
                            v-model="reviewData.finalOverallRating"
                            class="form-control"
                            min="1"
                            max="5"
                            step="0.01"
                            placeholder="Enter final overall rating"
                            required
                        >
                        <small class="text-muted">Consider both self and manager ratings</small>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fw-bold">HR/CEO Rating (Optional)</label>
                        <input
                            type="number"
                            v-model="reviewData.hrCeoRating"
                            class="form-control"
                            min="1"
                            max="5"
                            step="0.01"
                            placeholder="HR/CEO rating if applicable"
                        >
                      </div>
                    </div>

                    <div class="row mb-3">
                      <div class="col-12">
                        <label class="form-label fw-bold">Reviewer Comments <span class="text-danger">*</span></label>
                        <textarea
                            v-model="reviewData.reviewerComments"
                            class="form-control"
                            rows="4"
                            placeholder="Add your final comments, recommendations, or concerns..."
                            required
                        ></textarea>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-12">
                        <label class="form-label fw-bold">Decision <span class="text-danger">*</span></label>
                        <div class="form-check mb-2">
                          <input
                              class="form-check-input"
                              type="radio"
                              name="decision"
                              id="approve"
                              value="Approved"
                              v-model="reviewData.decision"
                          >
                          <label class="form-check-label" for="approve">
                            <i class="mdi mdi-check-circle text-success me-1"></i>
                            <strong>Approve</strong> - Appraisal is complete and accepted
                          </label>
                        </div>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="radio"
                              name="decision"
                              id="reject"
                              value="Rejected"
                              v-model="reviewData.decision"
                          >
                          <label class="form-check-label" for="reject">
                            <i class="mdi mdi-close-circle text-danger me-1"></i>
                            <strong>Reject</strong> - Send back for revision
                          </label>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <router-link to="/hr-dashboard" class="btn btn-secondary">
                        <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
                      </router-link>
                      <div>
                        <button @click="submitReview" class="btn btn-success btn-lg" :disabled="submitting">
                          <span v-if="submitting">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Processing...
                          </span>
                          <span v-else>
                            <i class="mdi mdi-check-all me-1"></i> Submit Final Review
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Not Found -->
          <div v-else class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline display-4 text-muted"></i>
            <h5 class="mt-3">Appraisal Not Found</h5>
            <p class="text-muted">The appraisal you're trying to review doesn't exist.</p>
            <router-link to="/hr-dashboard" class="btn btn-primary mt-3">
              <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
            </router-link>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "HRReviewForm",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      submitting: false,
      appraisalId: null,
      appraisal: null,
      kpas: [],
      organizationalParams: [],
      attributes: [],
      jobContent: [],
      growthOpportunities: [],
      workCulture: [],
      colleagues: [],
      trainings: [],
      additionalFeedback: null,
      goals: [],
      reviewData: {
        finalOverallRating: '',
        hrCeoRating: '',
        reviewerComments: '',
        decision: 'Approved'
      }
    };
  },

  mounted() {
    this.appraisalId = this.$route.query.id;
    if (this.appraisalId) {
      this.loadAppraisal();
    }
  },

  methods: {
    async loadAppraisal() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalForHRReview");
        data.append("appraisalId", this.appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const details = res.data.data;

          this.appraisal = details.appraisal;
          this.kpas = details.kpas || [];
          this.organizationalParams = details.organizationalParams || [];
          this.attributes = details.attributes || [];
          this.jobContent = details.jobContent || [];
          this.growthOpportunities = details.growthOpportunities || [];
          this.workCulture = details.workCulture || [];
          this.colleagues = details.colleagues || [];
          this.trainings = details.trainings || [];
          this.additionalFeedback = details.additionalFeedback;
          this.goals = details.goals || [];

          // Pre-calculate suggested rating
          this.calculateSuggestedRating();
        } else {
          this.appraisal = null;
          this.$toast?.error("Failed to load appraisal") || alert("Failed to load appraisal");
        }
      } catch (error) {
        console.error("Failed to load appraisal:", error);
        this.$toast?.error("Error loading appraisal") || alert("Error loading appraisal");
      } finally {
        this.loading = false;
      }
    },

    calculateSuggestedRating() {
      const selfRating = parseFloat(this.appraisal.sectionA_SelfOverallRating) || 0;
      const managerRating = parseFloat(this.appraisal.sectionA_AppraiserOverallRating) || 0;

      if (selfRating && managerRating) {
        // Suggest average of self and manager ratings
        const suggested = ((selfRating + managerRating) / 2).toFixed(2);
        this.reviewData.finalOverallRating = suggested;
      }
    },

    async submitReview() {
      // Validation
      if (!this.reviewData.finalOverallRating) {
        this.$toast?.error("Please provide final overall rating") || alert("Please provide final overall rating");
        return;
      }

      if (!this.reviewData.reviewerComments) {
        this.$toast?.error("Please add reviewer comments") || alert("Please add reviewer comments");
        return;
      }

      if (!this.reviewData.decision) {
        this.$toast?.error("Please select a decision (Approve/Reject)") || alert("Please select a decision");
        return;
      }

      this.submitting = true;

      try {
        const data = new FormData();
        data.append("function", "submitHRReview");
        data.append("appraisalId", this.appraisalId);
        data.append("reviewData", JSON.stringify(this.reviewData));

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Review submitted successfully!") || alert("Review submitted successfully!");
          this.$router.push('/hr-dashboard');
        } else {
          this.$toast?.error(res.data?.message || "Failed to submit review") || alert("Failed to submit review");
        }
      } catch (error) {
        console.error("Failed to submit review:", error);
        this.$toast?.error("Error submitting review") || alert("Error submitting review");
      } finally {
        this.submitting = false;
      }
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-info',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-warning',
        'Reviewer Pending': 'bg-warning',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-primary',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
  color: #000;
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}
</style>